// components/patient/PatientPortalLayout.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { 
  createPatientSession, 
  validateSession, 
  recordActivity, 
  updateTimeSpent, 
  markNeedsRefresh,
  initializeSession,
  selectCurrentSession,
  selectIsSessionActive,
  selectSessionId,
  selectNeedsRefresh,
  selectSessionError
} from '../../store/patientSessionSlice';
import { patientUpdatesStream } from '../../services/patientApi';
import { LoadingSpinner } from '../common/LoadingSpinner';

// Icons
import { 
  HeartIcon, 
  ChatBubbleLeftEllipsisIcon,
  ClockIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

interface PatientPortalLayoutProps {
  children: React.ReactNode;
  title?: string;
  showHeader?: boolean;
  showSessionInfo?: boolean;
}

const PatientPortalLayout: React.FC<PatientPortalLayoutProps> = ({
  children,
  title = "Patient Feedback Portal",
  showHeader = true,
  showSessionInfo = true,
}) => {
  const dispatch = useAppDispatch();
  const currentSession = useAppSelector(selectCurrentSession);
  const isSessionActive = useAppSelector(selectIsSessionActive);
  const sessionId = useAppSelector(selectSessionId);
  const needsRefresh = useAppSelector(selectNeedsRefresh);
  const sessionError = useAppSelector(selectSessionError);
  
  const [isInitializing, setIsInitializing] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const sessionStartTime = useRef<Date>(new Date());
  const activityTimer = useRef<NodeJS.Timeout | null>(null);

  // Initialize session on component mount
  useEffect(() => {
    initializePatientSession();
  }, []);

  // Set up activity tracking
  useEffect(() => {
    if (isSessionActive) {
      setupActivityTracking();
      connectToUpdatesStream();
    }

    return () => {
      if (activityTimer.current) {
        clearInterval(activityTimer.current);
      }
      patientUpdatesStream.disconnect();
    };
  }, [isSessionActive]);

  // Handle session refresh when needed
  useEffect(() => {
    if (needsRefresh && isSessionActive) {
      setShowSessionWarning(true);
      // Auto-refresh after 5 seconds if user doesn't interact
      setTimeout(() => {
        if (needsRefresh) {
          handleRefreshSession();
        }
      }, 5000);
    }
  }, [needsRefresh]);

  const initializePatientSession = async () => {
    try {
      // Check for existing session in localStorage
      const storedSession = localStorage.getItem('patient_session');
      
      if (storedSession) {
        const sessionData = JSON.parse(storedSession);
        const expiryTime = new Date(sessionData.expiresAt);
        
        if (expiryTime > new Date()) {
          // Session is still valid, initialize from storage
          dispatch(initializeSession(sessionData));
          
          // Validate with server
          try {
            await dispatch(validateSession()).unwrap();
          } catch (error) {
            // Session invalid on server, create new one
            await createNewSession();
          }
        } else {
          // Session expired, create new one
          localStorage.removeItem('patient_session');
          await createNewSession();
        }
      } else {
        // No existing session, create new one
        await createNewSession();
      }
    } catch (error) {
      console.error('Failed to initialize session:', error);
    } finally {
      setIsInitializing(false);
    }
  };

  const createNewSession = async () => {
    try {
      await dispatch(createPatientSession({
        language: navigator.language.split('-')[0] || 'en',
        allow_analytics: false,
        allow_follow_up: false,
        contact_method: 'none',
      })).unwrap();
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  };

  const setupActivityTracking = () => {
    // Track page activity
    const handleActivity = (type: string) => {
      dispatch(recordActivity({ 
        type, 
        page: window.location.pathname 
      }));
    };

    // Track various user interactions
    const events = ['click', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, () => handleActivity(event), { passive: true });
    });

    // Track time spent (update every 30 seconds)
    activityTimer.current = setInterval(() => {
      dispatch(updateTimeSpent(30)); // 30 seconds
    }, 30000);

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        handleActivity('page_hidden');
      } else {
        handleActivity('page_visible');
      }
    });
  };

  const connectToUpdatesStream = () => {
    patientUpdatesStream.connect();

    // Handle session updates
    patientUpdatesStream.on('session_update', (data) => {
      if (data.action === 'expires_soon') {
        dispatch(markNeedsRefresh());
      }
    });
  };

  const handleRefreshSession = async () => {
    try {
      // This would trigger the refresh in the slice
      setShowSessionWarning(false);
    } catch (error) {
      console.error('Failed to refresh session:', error);
    }
  };

  const getTimeRemaining = (): string => {
    if (!currentSession?.expires_at) return 'Unknown';
    
    const now = new Date();
    const expiry = new Date(currentSession.expires_at);
    const diff = expiry.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const getSessionStatus = () => {
    if (!isSessionActive) {
      return { 
        text: 'Session Inactive', 
        icon: ExclamationTriangleIcon, 
        color: 'text-red-500' 
      };
    }
    
    if (needsRefresh) {
      return { 
        text: 'Session Expiring', 
        icon: ClockIcon, 
        color: 'text-yellow-500' 
      };
    }
    
    return { 
      text: 'Session Active', 
      icon: ShieldCheckIcon, 
      color: 'text-green-500' 
    };
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-900">Initializing secure session...</p>
        </div>
      </div>
    );
  }

  if (sessionError && !isSessionActive) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Session Error
          </h2>
          <p className="text-gray-900 mb-6">
            {sessionError}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const status = getSessionStatus();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Session expiry warning */}
      {showSessionWarning && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex items-center justify-between">
            <div className="flex">
              <ClockIcon className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Your session will expire soon. 
                  <button
                    onClick={handleRefreshSession}
                    className="font-medium underline hover:text-yellow-600 ml-1"
                  >
                    Extend session
                  </button>
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowSessionWarning(false)}
              className="text-yellow-400 hover:text-yellow-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      {showHeader && (
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center">
                <HeartIcon className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                  <p className="text-sm text-gray-900">Secure & Confidential</p>
                </div>
              </div>

              {/* Session info */}
              {showSessionInfo && (
                <div className="flex items-center space-x-4">
                  <div className="flex items-center text-sm">
                    <status.icon className={`h-4 w-4 ${status.color} mr-2`} />
                    <span className="text-gray-900">{status.text}</span>
                  </div>
                  
                  {isSessionActive && (
                    <div className="text-sm text-gray-800">
                      Expires in: {getTimeRemaining()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-800">
              <ShieldCheckIcon className="h-4 w-4 mr-2" />
              <span>HIPAA-Ready & Secure</span>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-800">
              <button
                onClick={() => window.open('/patient/privacy', '_blank')}
                className="hover:text-blue-600"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => window.open('/patient/help', '_blank')}
                className="hover:text-blue-600"
              >
                Help & Support
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Accessibility announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        role="status"
      >
        {needsRefresh && "Your session will expire soon"}
        {sessionError && `Session error: ${sessionError}`}
      </div>
    </div>
  );
};

export default PatientPortalLayout;